import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowBack, IoCheckmarkCircle, IoAdd, IoTrash, IoInformationCircle } from "react-icons/io5";

const EditProductScreen = () => {
  const navigate = useNavigate();
  const fileInputRefs = useRef([]);
  const timeoutRef = useRef();

  const [images, setImages] = useState(Array(9).fill(null));
  const [quantity, setQuantity] = useState("50");
  const [showSuccess, setShowSuccess] = useState(false);

  const product = {
    name: "Premium Wireless Headphones",
    category: "Electronics",
    brand: "Sony",
    color: "Black",
    description: "High-quality wireless headphones with noise cancellation",
    size: "Medium",
    weight: "250g",
    sellingPrice: "25,000",
    material: "Plastic and Metal",
    note: "Handle with care",
    ageGroup: "Adults",
    gender: "Unisex",
  };

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const handleImageUpload = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImages = [...images];
        newImages[index] = e.target.result;
        setImages(newImages);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageRemove = (index) => {
    const newImages = [...images];
    newImages[index] = null;
    setImages(newImages);
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].value = "";
    }
  };

  const handleSlotClick = (index) => {
    fileInputRefs.current[index]?.click();
  };

  const isProductInfoComplete = images.some((img) => img !== null);
  const isVariationComplete = quantity.trim() !== "";

  const handleUpdateProduct = () => {
    setShowSuccess(true);
    timeoutRef.current = setTimeout(() => {
      setShowSuccess(false);
      navigate('/products');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {showSuccess && (
        <div className="fixed inset-0 z-50 bg-black/15 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl p-6 flex flex-col items-center gap-3 min-w-[260px]">
            <p className="text-lg font-medium text-center">Product Updated Successfully</p>
            <IoCheckmarkCircle className="w-8 h-8 text-[#19C463]" />
          </div>
        </div>
      )}

      <header className="fixed top-0 left-0 right-0 z-10 flex items-center gap-4 bg-white px-6 py-4 shadow-sm">
        <button onClick={() => navigate(-1)} className="text-gray-900">
          <IoArrowBack className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-semibold text-gray-900">Edit Product</h1>
      </header>

      <main className="px-6 pt-20 pb-6">
        <div className="mx-auto max-w-2xl space-y-6">
          <section className="rounded-lg bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Product Information</h2>
              <IoCheckmarkCircle
                className={`h-6 w-6 ${isProductInfoComplete ? "text-[#19C463]" : "text-[#AAAAAA]"}`}
              />
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              {images.map((image, index) => (
                <div key={index} className="relative">
                  <input
                    type="file"
                    ref={(el) => (fileInputRefs.current[index] = el)}
                    onChange={(e) => handleImageUpload(index, e)}
                    accept="image/*"
                    className="hidden"
                  />
                  {image ? (
                    <div className="relative aspect-square">
                      <img
                        loading="lazy"
                        src={image}
                        alt={`Product ${index + 1}`}
                        onClick={() => handleSlotClick(index)}
                        className="h-full w-full cursor-pointer rounded-lg object-cover"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageRemove(index);
                        }}
                        className="absolute -right-2 -top-2 rounded-full bg-white p-1 shadow-md"
                      >
                        <IoTrash className="h-4 w-4 text-[#19C463]" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => handleSlotClick(index)}
                      className="relative flex aspect-square cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-[#19C463] bg-white"
                    >
                      <IoAdd className="h-8 w-8 text-[#19C463]" />
                      <span className="mt-1 text-xs text-[#19C463]">Main Image</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-lg bg-green-50 p-3">
              <IoInformationCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#19C463]" />
              <div>
                <p className="text-sm text-gray-700">Click on the image again to change the picture</p>
                <p className="mt-1 text-xs text-gray-600">
                  Upload up to 9 images. Only 1 image is required. Supported formats: JPG, PNG. Max size: 5MB each.
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Product Name</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.name}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Category</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.category}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Brand</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.brand}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Color</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.color}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Product Description</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.description}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-lg bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Product Variation</h2>
              <IoCheckmarkCircle
                className={`h-6 w-6 ${isVariationComplete ? "text-[#19C463]" : "text-[#AAAAAA]"}`}
              />
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Size</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.size}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Weight</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.weight}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Quantity</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Selling Price</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">₦{Number(String(product.sellingPrice || 0).replace(/,/g, "")).toLocaleString("en-NG")}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-lg bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Product Specification</h2>
              <IoCheckmarkCircle className="h-6 w-6 text-[#19C463]" />
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Material</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.material}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Note</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.note}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Age Group</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.ageGroup}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Gender</label>
                <div className="mt-1 rounded-lg bg-[#AAAAAA] px-3 py-2">
                  <span className="text-sm text-white">{product.gender}</span>
                </div>
              </div>
            </div>
          </section>

          <button
            onClick={handleUpdateProduct}
            className={`w-full rounded-lg py-3 text-sm font-semibold transition-colors ${quantity.trim() === '' ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#19C463] text-white active:bg-[#14a352]'}`}
            disabled={quantity.trim() === ''}
          >
            Update Product
          </button>
        </div>
      </main>
    </div>
  );
};

export default EditProductScreen;


